import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * 内联颜色选择器组件
 * 替代浏览器原生 <input type="color">，样式可控
 * 
 * @param {string} color - 当前颜色值 (hex)
 * @param {function} onChange - 颜色改变回调 (hex)
 * @param {object} triggerStyle - 触发按钮的额外样式
 * @param {React.ReactNode} children - 自定义触发按钮内容
 * @param {boolean} rainbow - 是否显示彩虹渐变触发按钮
 * @param {number} size - 触发按钮大小
 */
const InlineColorPicker = ({ color = '#ff0000', onChange, triggerStyle, children, rainbow = false, size = 18, onMouseDown, onPointerDown }) => {
  const [open, setOpen] = useState(false);
  const [hsv, setHsv] = useState(() => hexToHsv(color));
  const [hexInput, setHexInput] = useState(color);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const gradientRef = useRef(null);
  const hueRef = useRef(null);
  const draggingSV = useRef(false);
  const draggingHue = useRef(false);

  // 同步外部 color 变化
  useEffect(() => {
    if (!open) {
      const newHsv = hexToHsv(color);
      setHsv(newHsv);
      setHexInput(color);
    }
  }, [color, open]);

  // 关闭弹窗
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
  }, [open]);

  // SV 面板拖拽
  const handleSVStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingSV.current = true;
    updateSV(e);
  }, [hsv.h]);

  const updateSV = useCallback((e) => {
    const rect = gradientRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const newHsv = { h: hsv.h, s: x, v: 1 - y };
    setHsv(newHsv);
    const hex = hsvToHex(newHsv);
    setHexInput(hex);
    onChange?.(hex);
  }, [hsv.h, onChange]);

  // Hue 条拖拽
  const handleHueStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingHue.current = true;
    updateHue(e);
  }, [hsv.s, hsv.v, onChange]);

  const updateHue = useCallback((e) => {
    const rect = hueRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newHsv = { h: x * 360, s: hsv.s, v: hsv.v };
    setHsv(newHsv);
    const hex = hsvToHex(newHsv);
    setHexInput(hex);
    onChange?.(hex);
  }, [hsv.s, hsv.v, onChange]);

  // 全局拖拽
  useEffect(() => {
    if (!open) return;
    const handleMove = (e) => {
      if (draggingSV.current) updateSV(e);
      if (draggingHue.current) updateHue(e);
    };
    const handleUp = () => {
      draggingSV.current = false;
      draggingHue.current = false;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [open, updateSV, updateHue]);

  // Hex 输入
  const handleHexChange = (val) => {
    setHexInput(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      setHsv(hexToHsv(val));
      onChange?.(val);
    }
  };

  // 计算弹窗位置
  const getPopoverPos = () => {
    if (!triggerRef.current) return { top: 0, left: 0 };
    const rect = triggerRef.current.getBoundingClientRect();
    const pw = 180, ph = 200;
    let top = rect.bottom + 4;
    let left = rect.left - pw / 2 + rect.width / 2;
    // 边界检测
    if (left + pw > window.innerWidth - 8) left = window.innerWidth - pw - 8;
    if (left < 8) left = 8;
    if (top + ph > window.innerHeight - 8) top = rect.top - ph - 4;
    return { top, left };
  };

  const currentHex = hsvToHex(hsv);
  const hueColor = hsvToHex({ h: hsv.h, s: 1, v: 1 });
  const rgb = hexToRgb(currentHex);

  return (
    <>
      <div
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        onMouseDown={onMouseDown}
        onPointerDown={onPointerDown}
        style={{
          position: 'relative', width: size, height: size, cursor: 'pointer',
          ...triggerStyle
        }}
      >
        {children || (rainbow ? (
          <div style={{
            width: size, height: size, borderRadius: '50%',
            background: 'conic-gradient(from 0deg, #ff0000, #ff7f00, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
            boxShadow: '0 0 0 1px #444'
          }} />
        ) : (
          <div style={{
            width: size, height: size, borderRadius: '3px',
            background: color, border: '1px solid #444'
          }} />
        ))}
      </div>

      {open && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            ...getPopoverPos(),
            width: 180,
            background: '#2a2a2a',
            border: '1px solid #444',
            borderRadius: '6px',
            padding: '8px',
            zIndex: 1000001,
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            userSelect: 'none'
          }}
          onMouseDown={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
        >
          {/* SV 渐变面板 */}
          <div
            ref={gradientRef}
            onMouseDown={handleSVStart}
            style={{
              width: '100%', height: 110, borderRadius: '4px', position: 'relative', cursor: 'crosshair',
              background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})`
            }}
          >
            {/* 选择指示器 */}
            <div style={{
              position: 'absolute',
              left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`,
              width: 10, height: 10, borderRadius: '50%',
              border: '2px solid #fff', boxShadow: '0 0 2px rgba(0,0,0,0.6)',
              transform: 'translate(-50%, -50%)', pointerEvents: 'none'
            }} />
          </div>

          {/* Hue 条 */}
          <div
            ref={hueRef}
            onMouseDown={handleHueStart}
            style={{
              width: '100%', height: 10, borderRadius: '5px', marginTop: '6px', cursor: 'pointer', position: 'relative',
              background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)'
            }}
          >
            <div style={{
              position: 'absolute',
              left: `${(hsv.h / 360) * 100}%`, top: '50%',
              width: 12, height: 12, borderRadius: '50%',
              border: '2px solid #fff', boxShadow: '0 0 2px rgba(0,0,0,0.6)',
              transform: 'translate(-50%, -50%)', pointerEvents: 'none',
              background: hueColor
            }} />
          </div>

          {/* 颜色预览 + Hex + 吸管 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
            {/* 预览色块 */}
            <div style={{
              width: 20, height: 20, borderRadius: '3px', flexShrink: 0,
              background: currentHex, border: '1px solid #555'
            }} />
            {/* Hex 输入 */}
            <input
              type="text"
              value={hexInput}
              onChange={e => handleHexChange(e.target.value)}
              onFocus={e => e.target.select()}
              style={{
                flex: 1, background: '#1e1e1e', border: '1px solid #444', borderRadius: '3px',
                color: '#ccc', fontSize: '11px', padding: '2px 4px', outline: 'none',
                fontFamily: 'monospace', width: 0
              }}
            />
            {/* 吸管工具 */}
            {typeof window !== 'undefined' && window.EyeDropper && (
              <div
                title="吸管取色"
                onClick={async () => {
                  try {
                    const dropper = new window.EyeDropper();
                    const result = await dropper.open();
                    const c = result.sRGBHex;
                    setHsv(hexToHsv(c));
                    setHexInput(c);
                    onChange?.(c);
                  } catch (e) { /* 用户取消 */ }
                }}
                style={{
                  width: 20, height: 20, borderRadius: '3px', flexShrink: 0,
                  background: '#1e1e1e', border: '1px solid #444',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'background 0.1s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#333'}
                onMouseLeave={e => e.currentTarget.style.background = '#1e1e1e'}
              >
                {/* 吸管 SVG 图标 */}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m2 22 1-1h3l9-9" />
                  <path d="M3 21v-3l9-9" />
                  <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9" />
                  <path d="m15 6 4 4" />
                  <path d="m18 9-4-4" />
                </svg>
              </div>
            )}
          </div>

          {/* RGB 数值 */}
          <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
            {[
              { label: 'R', value: rgb.r, key: 'r' },
              { label: 'G', value: rgb.g, key: 'g' },
              { label: 'B', value: rgb.b, key: 'b' }
            ].map(ch => (
              <div key={ch.key} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '2px' }}>
                <span style={{ fontSize: '10px', color: '#888', flexShrink: 0 }}>{ch.label}</span>
                <input
                  type="number" min="0" max="255"
                  value={ch.value}
                  onChange={e => {
                    const v = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                    const newRgb = { ...rgb, [ch.key]: v };
                    const hex = rgbToHex(newRgb);
                    setHsv(hexToHsv(hex));
                    setHexInput(hex);
                    onChange?.(hex);
                  }}
                  style={{
                    width: 0, flex: 1, background: '#1e1e1e', border: '1px solid #444', borderRadius: '3px',
                    color: '#ccc', fontSize: '10px', padding: '1px 3px', outline: 'none',
                    fontFamily: 'monospace', textAlign: 'center',
                    MozAppearance: 'textfield', WebkitAppearance: 'none'
                  }}
                />
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// --- 颜色转换工具函数 ---
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
}

function hexToHsv(hex) {
  const { r, g, b } = hexToRgb(hex);
  const rr = r / 255, gg = g / 255, bb = b / 255;
  const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rr) h = ((gg - bb) / d + 6) % 6;
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
    h *= 60;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

function hsvToHex({ h, s, v }) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return rgbToHex({
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  });
}

export default InlineColorPicker;
